import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { buildLogoutUrl } from '../auth/swa-auth';

interface LayoutProps {
  children: ReactNode;
  /** Rendered in the header when a session exists. */
  user?: { displayName: string; role: string };
  showLogout?: boolean;
}

/**
 * Areas reachable from the header.
 *
 * Only what actually works belongs here. A menu entry that leads nowhere is
 * the kind of sham the platform's fourth ground rule forbids - the areas of
 * phase 2 appear as each one is built, not before.
 */
const NAV_ITEMS: ReadonlyArray<{ to: string; label: string }> = [
  { to: '/', label: 'Übersicht' },
  { to: '/kunden', label: 'Kunden' },
];

export function Layout({ children, user, showLogout = false }: LayoutProps): ReactNode {
  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__bar">
          <div className="app-header__brand">
            <img className="app-header__logo" src="/oscom-logo-weiss.svg" alt="OSCOM" />
            <span className="app-header__subtitle">Technische Dokumentation</span>
          </div>
          {user ? (
            <div className="app-header__user">
              <span className="app-header__username">{user.displayName}</span>
              <span className="badge badge--onDark">{user.role}</span>
              {showLogout ? (
                // A plain link: the platform handles sign-out, the SPA does not
                // manage tokens or cookies itself.
                <a className="button button--onDark" href={buildLogoutUrl('/login')}>
                  Abmelden
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
        {user ? (
          <nav className="app-nav" aria-label="Bereiche">
            <ul>
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      isActive ? 'app-nav__link is-active' : 'app-nav__link'
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </header>
      <main className="app-main">{children}</main>
      <footer className="app-footer">
        <span>Interne Plattform</span>
        <span>Alle Inhalte vertraulich</span>
      </footer>
    </div>
  );
}
